import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CONTENT_RATINGS, type Episode } from '@gobingebox/shared';
import { cn } from '@/lib/cn';
import { posterGradient } from '@/lib/posters';
import { Skeleton } from '@/components/Skeleton';
import { buttonVariants } from '@/components/ui/Button';
import { Calendar, Check, Clock, Play, Plus } from '@/components/icons';
import { catalogApi, meApi } from '@/features/catalog/api';
import { Comments } from '@/features/comments/Comments';
import { useAuth } from '@/features/auth/AuthContext';

function isUpcoming(ep: Episode): boolean {
  return ep.releaseAt !== null && new Date(ep.releaseAt).getTime() > Date.now();
}
function minutes(sec: number | null): string {
  return sec ? `${Math.round(sec / 60)}m` : '';
}
function dateLabel(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
}

export function ShowPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const qc = useQueryClient();

  const showQ = useQuery({ queryKey: ['show', slug], queryFn: () => catalogApi.show(slug) });
  const listQ = useQuery({ queryKey: ['my-list'], queryFn: meApi.list, enabled: Boolean(user) });

  const inList = Boolean(showQ.data && listQ.data?.shows.some((s) => s.id === showQ.data.show.id));
  const toggle = useMutation({
    mutationFn: async () => {
      const id = showQ.data!.show.id;
      return inList ? meApi.removeFromList(id) : meApi.addToList(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-list'] }),
  });

  if (showQ.isLoading) {
    return (
      <div className="pb-16">
        <Skeleton className="h-[44vh] w-full" />
        <div className="container-page mt-8 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-card" />
          ))}
        </div>
      </div>
    );
  }
  if (showQ.isError || !showQ.data) {
    return (
      <div className="container-page py-20 text-center text-ink-muted">
        We couldn't find that show.{' '}
        <Link to="/browse" className="font-semibold text-brand-soft underline">
          Back to Browse
        </Link>
      </div>
    );
  }

  const { show, episodes } = showQ.data;
  // Watchable = released AND has an uploaded video.
  const playable = episodes.find((e) => !isUpcoming(e) && Boolean(e.streamUid));
  const previewEp = playable ?? episodes[0];
  const hasPreview = Boolean(show.previewStreamUid) && Boolean(previewEp);
  const nothingToWatch = !playable && !hasPreview;

  return (
    <div className="pb-16">
      {/* Hero */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <div className="absolute inset-0" style={{ backgroundImage: posterGradient(show.id) }} />
          <div className="absolute inset-0 bg-gradient-to-r from-bg via-bg/75 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-hero-fade" />
        </div>
        <div className="container-page relative z-10 flex min-h-[42vh] flex-col justify-end py-12">
          <div className="max-w-2xl">
            <h1 className="font-display text-display-sm font-extrabold text-ink">{show.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-border bg-surface/70 px-2.5 py-1 text-xs font-medium text-ink-muted">
                JP · EN audio
              </span>
              {show.rating && (
                <span className="inline-flex items-center rounded-full border border-brand/40 bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand-soft">
                  {CONTENT_RATINGS.find((r) => r.value === show.rating)?.label ?? show.rating}
                </span>
              )}
              {show.genres.map((g) => (
                <Link
                  key={g.id}
                  to={`/search?q=${encodeURIComponent(g.name)}`}
                  className="inline-flex items-center rounded-full border border-border bg-surface/70 px-2.5 py-1 text-xs font-medium text-ink-muted hover:text-ink"
                >
                  {g.name}
                </Link>
              ))}
            </div>
            <p className="mt-4 max-w-prose text-ink-muted">{show.description}</p>

            <div className="mt-7 flex flex-wrap items-center gap-3">
              {playable && (
                <Link
                  to={`/shows/${show.slug}/${playable.number}`}
                  className={buttonVariants({ variant: 'primary', size: 'lg' })}
                >
                  <Play size={18} />
                  Play S{playable.season} E{playable.number}
                </Link>
              )}
              {hasPreview && previewEp && (
                <Link
                  to={`/shows/${show.slug}/${previewEp.number}?mode=preview`}
                  className={buttonVariants({ variant: 'secondary', size: 'lg' })}
                >
                  Watch Preview
                </Link>
              )}
              {nothingToWatch && (
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-ink-muted">
                  <Calendar size={16} className="text-brand-soft" />
                  Coming soon
                </span>
              )}
              {user && (
                <button
                  type="button"
                  onClick={() => toggle.mutate()}
                  disabled={toggle.isPending || listQ.isLoading}
                  className={buttonVariants({ variant: 'outline', size: 'lg' })}
                >
                  {inList ? <Check size={18} /> : <Plus size={18} />}
                  {inList ? 'In My List' : 'My List'}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Episodes */}
      <div className="container-page mt-10 grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <section>
          <h2 className="mb-4 font-display text-xl font-bold text-ink">Episodes</h2>
          {episodes.length === 0 ? (
            <div className="card px-6 py-12 text-center">
              <p className="font-display text-base font-bold text-ink">No episodes announced yet</p>
              <p className="mt-1 text-sm text-ink-muted">
                {show.status === 'upcoming'
                  ? "This show is in the works — episodes and release dates will appear here once they're scheduled."
                  : 'Check back soon.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {episodes.map((ep) => {
                const upcoming = isUpcoming(ep);
                const hasVideo = Boolean(ep.streamUid);
                return (
                  <li key={ep.id} className="card flex items-center gap-4 p-4">
                    <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-surface-raised font-display text-sm font-bold text-ink-muted">
                      {ep.number}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-ink">{ep.title}</p>
                      <p className="truncate text-sm text-ink-muted">{ep.description}</p>
                    </div>
                    {upcoming ? (
                      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-surface-raised px-3 py-1.5 text-xs font-medium text-brand-soft">
                        <Calendar size={14} />
                        Coming {dateLabel(ep.releaseAt)}
                      </span>
                    ) : hasVideo ? (
                      <div className="flex shrink-0 items-center gap-3">
                        {ep.durationSeconds && (
                          <span className="hidden items-center gap-1 text-xs text-ink-faint sm:inline-flex">
                            <Clock size={13} />
                            {minutes(ep.durationSeconds)}
                          </span>
                        )}
                        <Link
                          to={`/shows/${show.slug}/${ep.number}`}
                          className={buttonVariants({ variant: 'secondary', size: 'sm' })}
                        >
                          <Play size={15} />
                          Play
                        </Link>
                      </div>
                    ) : (
                      <span className="inline-flex shrink-0 items-center rounded-full bg-surface-raised px-3 py-1.5 text-xs font-medium text-ink-faint">
                        Coming soon
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Cast & credits */}
        <aside>
          <h2 className="mb-4 font-display text-xl font-bold text-ink">Cast &amp; credits</h2>
          {show.cast.length === 0 ? (
            <p className="text-sm text-ink-muted">Credits coming soon.</p>
          ) : (
            <ul className="card divide-y divide-border">
              {show.cast.map((c, i) => (
                <li key={i} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-ink">{c.name}</p>
                    <p className="text-xs text-ink-faint">{c.role}</p>
                  </div>
                  <span
                    className={cn(
                      'rounded-full px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide',
                      c.kind === 'voice' ? 'bg-brand/15 text-brand-soft' : 'bg-surface-raised text-ink-muted',
                    )}
                  >
                    {c.kind}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {/* Comments */}
      <div className="container-page">
        <Comments slug={show.slug} />
      </div>
    </div>
  );
}
